// src/services/auth.ts
import { api, EventLoginData } from "../lib/api";
import { AppError } from "../lib/errors";
import { storage } from "../lib/storage";
import { getDeviceParams } from "../lib/device";

export type BootstrapPhase = "checking" | "creatingUser" | "loggingIn" | "eventLogin";

export type BootstrapResult = {
  userId: string;
  secretKey?: string;
  accessToken: string;
  eventLogin?: EventLoginData;
};

export async function bootstrapAuth(opts?: {
  onPhase?: (phase: BootstrapPhase) => void;
}): Promise<BootstrapResult> {
  opts?.onPhase?.("checking");

  let { userId, secretKey, accessToken } = storage.getAuth();

  console.log("[bootstrapAuth] start:", { userId, hasSecretKey: !!secretKey, hasAccessToken: !!accessToken });

  if (!userId) {
    // userId がない → 完全な新規端末: ユーザー作成
    opts?.onPhase?.("creatingUser");
    const created = await api.createUser();
    userId = created.userId;
    secretKey = created.secretKey;
    storage.setAuth({ userId, secretKey });
    console.log("[bootstrapAuth] new user created:", userId);
  }

  if (!secretKey) {
    // userId はあるが secretKey がない → SSO ユーザー（Case B）
    // accessToken がなければセッション切れ
    if (!accessToken) {
      console.log("[bootstrapAuth] SSO user session expired");
      throw new AppError("Session expired. Please sign in again.", "SESSION_EXPIRED");
    }
    // accessToken があれば login をスキップしてそのまま続行
    console.log("[bootstrapAuth] SSO user with accessToken — skip login");
    opts?.onPhase?.("loggingIn");
    // SSO ユーザーは secretKey がないため api.login() をスキップ
    // accessToken はすでにストレージにあるためそのまま使用する
  } else {
    // 通常ユーザー: secretKey で login して accessToken を最新化
    opts?.onPhase?.("loggingIn");
    const loggedIn = await api.login({ userId, secretKey });
    accessToken = loggedIn.accessToken;
    storage.setAuth({ accessToken });
    console.log("[bootstrapAuth] login done, accessToken refreshed");
  }

  // 追加：GET /event/login（遷移前に必ず実行）
  opts?.onPhase?.("eventLogin");
  const device = getDeviceParams();
  let eventLogin: EventLoginData | undefined;
  try {
    eventLogin = await api.eventLogin({
      userId,
      terminalIdentificationNumber: device.terminalIdentificationNumber,
    });
  } catch (error) {
    // event/login は補助的な初期化APIのため、4xx の一部は起動を継続する
    if (
      !(error instanceof AppError) ||
      (error.code !== "BAD_REQUEST" && error.code !== "NOT_FOUND")
    ) {
      throw error;
    }
  }

  if (!accessToken) {
    throw new AppError("Session expired. Please sign in again.", "SESSION_EXPIRED");
  }

  return { userId, secretKey, accessToken, eventLogin };
}
